import { Component, OnDestroy, OnInit } from '@angular/core';
import { catchError, finalize, of, switchMap, takeUntil, tap } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';
import { SymbolPosition } from 'src/app/pages/position/models/symbol-position';
import { AccountService } from 'src/app/shared/services/account.service';
import { DataService } from 'src/app/shared/services/data.service';
import { PositionService } from 'src/app/shared/services/position.service';
import { AllocationRequest } from '../models/allocation-request';
import { Trade } from '../models/trade';
import { TradeSummaryPresentation } from '../models/trade-summary-presentation';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { DialogInputConfig, DialogResult } from 'src/app/shared/models/dialog-input';
import { DialogInputComponent } from 'src/app/shared/components/dialog-input/dialog-input.component';
import * as _ from 'lodash';
import { AllocationService } from 'src/app/shared/services/allocation.service';
import { MessagesService } from 'src/app/components/messages/messages.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
    selector: 'app-allocation-detail',
    templateUrl: './allocation-detail.component.html',
    styleUrl: './allocation-detail.component.scss',
    standalone: false
})
export class AllocationDetailComponent implements OnInit, OnDestroy {

    public currentTrade: Trade;
    public riaId: number;

    public accounts: TradeSummaryPresentation[];

    public displayedColumns: string[] = ['accountId', 'userNamed', 'symbol', 'side', 'quantity', 'price', 'quantityToAllocate', 'newQuantity', 'newPrice'];

    private onDestroy$ = new Subject<void>();
    public loading: boolean = true;

    public isSelectedQuantityValid: boolean = true;
    public percentage: number = 0;
    public expectedQuantity: number = 0;
    public selectedQuantity: number = 0;

    constructor(private data: DataService,
        private positionService: PositionService,
        public dialog: DialogService,
        private accountService: AccountService,
        private allocationService: AllocationService,
        private  messageService: MessagesService,
        private router: Router,
        private spinnerService: NgxSpinnerService
    ) { }

    ngOnInit() {
        this.spinnerService.show();
        this.data.currentRiaId$
            .pipe(
                switchMap(riaId => {
                    this.riaId = riaId;
                    return this.data.currentTrade$;
                }),
                catchError(error => this.handleFetchError(error)),
                tap(() => {
                    this.spinnerService.hide();
                    this.loading = false;
                }),
                takeUntil(this.onDestroy$)
            )
            .subscribe((trade: Trade) => {
                this.currentTrade = trade;
                this.currentTrade.posSide = this.currentTrade.side === 'B' || this.currentTrade.side === 'C' ? 'L' : this.currentTrade.side === 'S' || this.currentTrade.side === 'T' ? 'S' : '';

                let element = {
                    userNamed: this.currentTrade.userNamed,
                    side: this.currentTrade.side,
                    symbol: this.currentTrade.symbol,
                    accountPk: this.currentTrade.accountPk,
                    quantities: []
                }
                this.currentTrade.orders.forEach(order => {
                    if (order.checked) {
                        element.quantities.push({
                            orderID: order.orderId,
                            execQuantity: order.quantityToAllocate
                        });
                        this.expectedQuantity += order.quantityToAllocate;
                    }
                });

                this.spinnerService.show();
                this.accountService.getCombinedOrder(element).pipe(
                    finalize(()=> this.spinnerService.hide())
                )
                .subscribe(elements => {
                    // TODO: verify if we need this call at all
                    let symbolPosition: SymbolPosition = {
                        group: 0,
                        longShort: this.currentTrade.side,
                        riaId: this.riaId,
                        symbol: this.currentTrade.symbol,
                    };
                    this.positionService.getSymbolPosition(symbolPosition)
                        .subscribe(elements => {
                            this.accounts = elements;
                            this.accounts.map(account => {
                                account.quantity = account.quantity ?? 0;
                                account.price = account.price ?? 0;
                                account.symbol = account.symbol ?? this.currentTrade.symbol;
                                account.side = account.side ?? this.currentTrade.posSide;
                                account.newQuantity = account.quantity;
                                account.newPrice = account.price;
                                account.quantityToAllocate = 0;
                            });
                        });
                });
            });
    }

    public quantityToAllocateChanged(element: TradeSummaryPresentation, $event) {
        let value = Math.abs($event.srcElement.value);
        let maxValue = Math.abs(this.expectedQuantity);
        let totalAccountQuantityToAllocate = Math.abs(_.sumBy(this.accounts, account => { return account.accountId != element.accountId ? account.quantityToAllocate : 0;}));
        let availableQuantityToAllocate = totalAccountQuantityToAllocate < maxValue ?maxValue - totalAccountQuantityToAllocate : 0;
        let maxAvailableValue = maxValue > availableQuantityToAllocate ? availableQuantityToAllocate : maxValue;

        if (totalAccountQuantityToAllocate + value >= maxValue && maxAvailableValue === 0){
            this.openDialog("You don't have enough quantity to allocate to this account!");
            $event.srcElement.value = element.quantityToAllocate;
            return;
        }

        if ($event.srcElement.value < 0 || value > Math.abs(maxAvailableValue)) {
            this.openDialog(`The value you entered is out of range (0 - ${maxValue})<br \> Do you want to use the max possible value: ${maxAvailableValue}?`, "Confirm", "Yes", false,
            () => {
                $event.srcElement.value = maxAvailableValue;
                this.updateQuantityAndPercentage(element, maxAvailableValue);
            },
            ()=> {
                $event.srcElement.value = element.quantityToAllocate;
            });
        } else {
            this.updateQuantityAndPercentage(element, value);
        }
    }

    updateQuantityAndPercentage(element: TradeSummaryPresentation, value: number): void {
        // Update quantity and price
        element.newQuantity = this.computeNewQuantity(element, value);
        element.newPrice = this.computeNewPrice(element, value);
        element.quantityToAllocate = value;

        // Recalculate totals
        this.selectedQuantity = this.accounts.reduce((sum, account) => sum + (account.quantityToAllocate || 0), 0);
        this.percentage = this.selectedQuantity / this.expectedQuantity;
        this.isSelectedQuantityValid = this.selectedQuantity <= this.expectedQuantity;
    }

    private computeNewQuantity(element: TradeSummaryPresentation, quantityToAllocate: number): number {
        const currentQuantity = Math.abs(element.quantity);
        const isSellTrade = this.currentTrade.side === 'S';
        const isLongPosition = element.side === 'L';

        if (isSellTrade && isLongPosition) {
            return currentQuantity - quantityToAllocate;
        }
        return currentQuantity + quantityToAllocate;
    }

    private computeNewPrice(element: TradeSummaryPresentation, quantityToAllocate: number): number {
        const baseQty = element.quantity ?? 0;
        const basePrice = element.price ?? 0;
        const execPrice = this.currentTrade.avgPrice;
        const isSellTrade = this.currentTrade.side === 'S';
        const isLongPosition = element.side === 'L';

        // Return base price if no calculation needed
        if ((isSellTrade && isLongPosition) || (!isSellTrade && !isLongPosition)) {
            return basePrice;
        }

        // Return exec price if no base price exists
        if (basePrice === 0) {
            return execPrice;
        }

        // Calculate weighted average price
        const totalQty = baseQty + quantityToAllocate;
        return ((basePrice * baseQty) + (execPrice * quantityToAllocate)) / totalQty;
    }

    onReset() {
        this.selectedQuantity = 0;
        this.percentage = 0;
        this.isSelectedQuantityValid = true;
        this.accounts = this.accounts
            .map(a => {
                a.quantityToAllocate = 0;
                return a;
            });
    }

    getSelectedPercentage(): number {
        return (this.selectedQuantity*100)/this.expectedQuantity;
    }

    onAllocate() {
        let AllocationRequest: AllocationRequest[] = [];
        let orderIds = this.currentTrade.orders.filter(x => x.checked).map(x => x.orderId.toString());
        this.accounts.forEach(element => {
            if(element.quantityToAllocate > 0) {
                AllocationRequest.push({
                    dvpAccountNumber: this.currentTrade.accountId,
                    orderIds: orderIds,
                    side: this.currentTrade.side === 'B' ? 1 : 2,
                    symbol:  this.currentTrade.symbol,                
                    destinationAccountNumber: element.accountId,
                    quantity: element.quantityToAllocate
                });
            }
        });

        this.spinnerService.show();
        this.allocationService.createTradeAllocation(AllocationRequest).pipe(
            catchError((response: HttpErrorResponse) => {
                this.messageService.showError(`Allocation unsuccessful!!. Message: ${response?.error}`, 10000);
                throw response;
            }),
            finalize(()=> this.spinnerService.hide())
        )
        .subscribe(result => {
            this.messageService.showMessage('Allocation Created.',5000);     
            this.router.navigate(["/allocate/allocation-list"]);   
        });
    }

    private handleFetchError(error: any) {
        console.error("Error fetching data:", error);
        return of([]);
    }

    openDialog(text: string, alertTitle: string = 'Alert', confirmText: string = 'Ok', isAlertDialog: boolean = true, onConfirm?: Function, onCancel?: Function) {
        let dialogRef;
        let dialogConfig: DialogInputConfig;
        dialogConfig= {
            inputEnabled: false,
            maxInputLength: 200,
            title: alertTitle,
            confirmText: confirmText,
            isAlertDialog,
            text
        };
    
        dialogRef = this.dialog.open(DialogInputComponent, {
          data:dialogConfig,
          disableClose: true,
        });
    
    
        return dialogRef.afterClosed().subscribe((result: DialogResult) => {
          if(result){
            if(onConfirm){
                onConfirm();
            } 
          } else {
            if(onCancel){
                onCancel();
            } 
          }
        });
      }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}